/**
 * Kitchen tickets (KOTs). Port of web `printHelper.js` `getTickets` (:405-589), `printSaleTickets`
 * (:593-652) and `printKOT` (:328-391) — the canonical copies.
 *
 * v1 carried FOUR drifted copies of `getTickets` (web printHelper, web polling, mobile printHelper,
 * mobile polling). This is the one. Where they disagreed, web printHelper wins; the differences are
 * listed in docs/flows/PRINTING.md §9.
 */
import type { Plan, PrintContext, Skip, TicketsInput } from "../types";
import { ticketPrinters } from "../routing";
import { clone, isEmpty } from "../util";
import { planBill } from "./bill";

const COMPLETED = "Completed";
const PENDING = "Pending";
const FAILED = "Failed";
const CANCELLED = "Cancelled";

/**
 * A sale's tickets, each shaped as the library's ticket input: its own items, title, notes and the
 * sale fields a kitchen needs, with the print template's hide flags applied.
 *
 * `auto` is v1's third argument. Every v1 caller of `printSaleTickets` passes `true` — including the
 * manual reprint — so a reprinted ticket is titled "(Reprint)", never "(Manual Print)".
 */
export function buildTickets(sale: any, tickets: any[] | undefined, auto: boolean, ctx: PrintContext): any[] {
  if (isEmpty(tickets) || !Array.isArray(tickets)) return [];
  const settings = ctx.posSettings?.tickets || {};
  const groups = settings.groups;
  const template = settings.printTemplate || {};

  return tickets.map((ticket) => {
    const data: any = {
      saleId: sale?.mid,
      printed: ticket?.printed,
      printStatus: ticket?.printStatus,
      ticketNumber: ticket?.ticketNumber,
      sourceInfo: sale?.sourceInfo,
    };
    if (ticket.ticketStatus !== CANCELLED) {
      data.ticketNote1 = ticket?.ticketNote1;
      data.ticketNote2 = ticket?.ticketNote2;
    }

    // a ticket without its own items takes the sale's items stamped with its number
    let items: any[] = [];
    if (isEmpty(ticket?.saleItems)) {
      for (const item of sale?.saleItems || []) {
        if (item?.ticketNumber !== ticket?.ticketNumber) continue;
        const entry: any = {
          shortName: item?.shortName,
          quantity: item?.quantity,
          note: item?.note,
          variantValues: item?.variantValues,
        };
        if (!isEmpty(item?.itemOptionList)) {
          entry.itemOptionList = item.itemOptionList.map((option: any) => ({ name: option?.name, quantity: option?.quantity }));
        }
        items.push(entry);
      }
    } else {
      items = ticket.saleItems;
    }

    data.billTitle = ticket?.isBackOrderTicket ? "Backorder Ticket" : "Service Ticket";
    if (!isEmpty(ticket.groupName)) data.billTitle = data.billTitle + ":" + ticket.groupName;
    if (data.printStatus === COMPLETED) data.billTitle = data.billTitle + " (Reprint)";
    else if (ticket.ticketStatus === CANCELLED) data.billTitle = data.billTitle + " (Cancelled)";
    else if (sale.saleStatus === CANCELLED) data.billTitle = data.billTitle + " (Cancelled)";
    else if (auto !== true) data.billTitle = data.billTitle + "(Manual Print)";

    // v1 stamps a missing creation date onto the caller's ticket; only `updatedBy` ever reads it
    const createdBy = ticket.createdBy && !isEmpty(ticket.createdBy.date) ? ticket.createdBy : { date: ctx.now };

    data.itemCount = items.length;
    data.serviceLabel = sale?.serviceLabel;
    if (sale.deliveryInfo) {
      data.deliveryInfo = { mode: sale.deliveryInfo.mode, deliveryDate: sale.deliveryInfo.deliveryDate };
    }
    if (!data.serviceLabel || isEmpty(data.serviceLabel.label)) {
      let contactInfo = sale?.customerInfo?.contactInfo || {};
      if (contactInfo?.name?.firstName && data.serviceLabel) {
        // no table/label: the kitchen calls the order by the customer's first name
        data.serviceLabel = { ...clone(data.serviceLabel), label: contactInfo.name.firstName };
      }
      if (sale?.deliveryInfo) {
        contactInfo = sale.deliveryInfo.contactInfo || {};
        data.deliveryInfo.contactInfo = { name: contactInfo?.name };
      } else if (sale.customerInfo) {
        data.customerInfo = { contactInfo: { name: contactInfo?.name } };
      }
    }

    data.updatedBy = ticket?.updatedBy || createdBy;
    data.saleBy = sale?.saleBy;
    data.note = sale?.note;
    data.showChannelName = template.hideChannelInfo ? sale?.showChannelName : true;
    data.channelName = sale?.channelName;
    data.channelDefId = sale?.channelDefId;
    data.showNote = true;
    data.groupName = ticket?.groupName;
    data.isAggregatedTicket = ticket?.isAggregatedTicket;
    data.isTransferTicket = ticket?.isTransferTicket;
    data.isBackOrderTicket = ticket?.isBackOrderTicket;
    data.billNumber = data.ticketNumber;
    data.saleItems = items;
    data.tags = sale?.tags;
    if (ticket.ticketCancelReason) data.ticketCancelReason = ticket.ticketCancelReason;

    if (!isEmpty(data.groupName) && !isEmpty(groups)) {
      for (const group of groups) {
        if (group.name !== data.groupName) continue;
        if (group?.showBillBarcode) data.billNumberBarcode = sale?.billNumber || sale?.offlineNumber;
        if (group?.pinSupport) data.pinSupport = true;
        // `showItemBarcode: false` does NOT strip item barcodes — web printHelper has that line
        // commented out (web polling strips them). Kept as web; flagged in PRINTING.md §9.
        if (!isEmpty(group?.footer)) data.billFooter = group.footer;
      }
    }

    let billHeader: any;
    if (sale?.billHeader) {
      billHeader = {};
      if (isEmpty(sale.billHeader.brandName)) billHeader.businessName = sale.billHeader.businessName;
      billHeader.brandName = sale.billHeader.brandName;
    }
    data.billHeader = billHeader;

    if (template.hideBusinessInfo) delete data.billHeader;
    if (template.hideChannelInfo) {
      delete data.showChannelName;
      delete data.channelName;
    }
    if (template.hideServiceLabelInfo) delete data.serviceLabel;
    if (template.hideCustomerInfo) {
      delete data.customerInfo;
      delete data.deliveryInfo?.contactInfo;
    }
    if (template.hideDeliveryInfo) delete data.deliveryInfo;
    if (template.hideSourceInfo) delete data.sourceInfo;
    if (template.hideSaleByAndDate) {
      delete data.updatedBy;
      delete data.saleBy;
    }
    return data;
  });
}

/**
 * The two decisions v1 `printSaleTickets` makes before it looks at a single ticket, in its order:
 * an adhoc table on a channel that skips its tickets prints none, and a polling channel's tickets
 * belong to the designated print device's poller, never to this request.
 *
 * Both are a TERMINAL's. v1's poller (`polling.js`) checks neither: it IS the print device those
 * tickets were handed to, and a sale it skipped would never be claimed, so the server would list it
 * again every cycle. A manual reprint skips neither either.
 */
function terminalSkip(input: TicketsInput, ctx: PrintContext): Skip | undefined {
  const { sale, manual = false, source } = input;
  if (manual || source === "Poller") return undefined;

  const channel = ctx.channels[sale?.channelDefId];
  if (channel?.skipAdhocTicketCreation && sale?.serviceLabel?.isAdhoc) return { reason: "adhocTicketsSkipped" };

  const settings = ctx.posSettings?.tickets;
  const printDeviceId = settings?.printDeviceId;
  if (printDeviceId && (settings?.pollingChannelDefIds || []).includes(sale?.channelDefId)) {
    return { reason: printDeviceId === ctx.device.id ? "polledByThisDevice" : "polledByAnotherDevice" };
  }
  return undefined;
}

/**
 * Whether this request's KOTs are the print device's poller's to print — whichever device that is.
 *
 * It is v1 web `printSaleTickets`' RETURN value, answered synchronously, and a caller reads it:
 * web `MyOrderCard` prints an accepted order's closed bill only when this is false, because the
 * print device prints that bill itself. So the POS gate on `printSaleTickets` must return THIS — a
 * print request's promise is always truthy, and would stop that bill printing for every order.
 * v1 answered `false` for the adhoc skip and `undefined` for a sale without tickets; both read as
 * false, which is all a caller does with it. Mobile's v1 never answered at all.
 */
export function ticketsHandedToPoller(input: TicketsInput, ctx: PrintContext): boolean {
  const reason = terminalSkip(input, ctx)?.reason;
  return reason === "polledByThisDevice" || reason === "polledByAnotherDevice";
}

/**
 * v1 `printSaleTickets` + `printKOT`: whether this device prints the sale's tickets, which ones,
 * on which printers — and, for an automatic print, the status claim that has to be accepted first.
 */
export function planTickets(input: TicketsInput, ctx: PrintContext): Plan {
  const { sale, tickets = [], manual = false, source } = input;
  // v1 `printHelper`'s three skips are a TERMINAL's: `terminalSkip` holds the first two, and the
  // aggregated-KOT one is per ticket, in the loop below
  const terminal = !manual && source !== "Poller";

  const skip = terminalSkip(input, ctx);
  if (skip) return { pieces: [], skipped: [skip] };

  if (!sale || isEmpty(sale.tickets) || !Array.isArray(sale.tickets)) return { pieces: [], skipped: [] };
  const hasSomethingToPrint = sale.tickets.some((ticket: any) => manual || ticket.printStatus === PENDING);
  if (!hasSomethingToPrint) return { pieces: [], skipped: [] };

  const prepared = buildTickets(sale, tickets, true, ctx);
  const skipped: Skip[] = [];
  const toPrint: any[] = [];
  for (const ticket of prepared) {
    if (ticket?.isAggregatedTicket && ctx.posSettings?.printAggregatedKotOnMFROnly && terminal) continue;
    if (!ticketPrinters(ticket, ctx).length) {
      skipped.push({ reason: "noPrinterForTicket", ticketNumber: ticket?.ticketNumber });
      continue;
    }
    if (manual || ticket?.printStatus === PENDING || ticket?.printStatus === FAILED) toPrint.push(ticket);
  }
  if (!toPrint.length) return { pieces: [], skipped };

  const pieces = toPrint.flatMap((ticket) =>
    ticketPrinters(ticket, ctx).flatMap(({ printer, copies }) => {
      const plan = planBill({ sale: ticket, printer, copies }, ctx, "tickets");
      skipped.push(...plan.skipped);
      return plan.pieces;
    }),
  );

  return {
    pieces,
    skipped,
    // a manual reprint writes no status — v1 prints it straight away
    claim: manual
      ? undefined
      : {
          of: "tickets",
          saleId: prepared[0]?.saleId,
          infos: toPrint.map((ticket) => ({
            ticketNumber: ticket?.ticketNumber,
            printStatus: "Completed" as const,
            ...(source ? { printSource: source } : {}),
          })),
        },
  };
}
